import { useState } from "react";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormDialog } from "@/components/ui/form-dialog";
import { FormSection } from "@/components/ui/form-section";
import { useUpdateVenue } from "@/hooks/useVenues";
import { toast } from "sonner";
import type { Venue } from "@/types";
import { AddressAutocomplete } from "./AddressAutocomplete";

interface VenueEditDialogProps {
  venue: Venue | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function VenueEditForm({
  venue,
  open,
  onOpenChange,
}: {
  venue: Venue;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const updateVenue = useUpdateVenue();
  const [form, setForm] = useState({
    name: venue.name,
    address: venue.address,
    latitude: venue.latitude != null ? String(venue.latitude) : "",
    longitude: venue.longitude != null ? String(venue.longitude) : "",
  });

  const handleAddressSelect = (address: string, lat: number, lng: number) => {
    setForm((f) => ({
      ...f,
      address,
      latitude: String(lat),
      longitude: String(lng),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateVenue.mutateAsync({
        id: venue.id,
        body: {
          name: form.name,
          address: form.address,
          latitude: form.latitude ? parseFloat(form.latitude) : null,
          longitude: form.longitude ? parseFloat(form.longitude) : null,
        },
      });
      toast.success("Lokalizacja zaktualizowana");
      onOpenChange(false);
    } catch (err) {
      toast.error("Nie udało się zaktualizować lokalizacji", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edytuj lokalizację"
      size="sm"
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button type="submit" form="venue-edit-form" disabled={updateVenue.isPending}>
            {updateVenue.isPending ? "Zapisywanie..." : "Zapisz zmiany"}
          </Button>
        </>
      }
    >
      <form id="venue-edit-form" onSubmit={handleSubmit}>
        <FormSection icon={MapPin} title="Lokalizacja">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-venue-name">Nazwa</Label>
              <Input
                id="edit-venue-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-venue-address">Adres</Label>
              <AddressAutocomplete
                id="edit-venue-address"
                value={form.address}
                onChange={(address) => setForm((f) => ({ ...f, address }))}
                onSelect={handleAddressSelect}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="edit-venue-lat">Szerokość geo.</Label>
                <Input
                  id="edit-venue-lat"
                  type="number"
                  step="any"
                  value={form.latitude}
                  onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))}
                  placeholder="53.0817"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-venue-lng">Długość geo.</Label>
                <Input
                  id="edit-venue-lng"
                  type="number"
                  step="any"
                  value={form.longitude}
                  onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))}
                  placeholder="21.5667"
                />
              </div>
            </div>
          </div>
        </FormSection>
      </form>
    </FormDialog>
  );
}

export function VenueEditDialog({ venue, open, onOpenChange }: VenueEditDialogProps) {
  if (!venue) return null;
  return <VenueEditForm key={venue.id} venue={venue} open={open} onOpenChange={onOpenChange} />;
}
